import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  CustomObjectsModalComponent,
  ModalData,
} from './components/custom-objects-modal/custom-objects-modal.component';
import { GoogleMap, MapInfoWindow } from '@angular/google-maps';
import { select, Store } from '@ngrx/store';
import { MapCustomObjectSelectors } from '@states/map-custom-objects/map-custom-objects.selector-types';
import { MapCustomObjectsActions } from '@states/map-custom-objects/map-custom-objects.action-types';
import { MapCustomObjectsModels } from '@models/map-custom-objects.model';
import MapCustomObject = MapCustomObjectsModels.MapCustomObject;
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import MapCustomObjectUpdateData = MapCustomObjectsModels.MapCustomObjectUpdateData;
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';
import {
  AlgorithmInput,
  AlgorithmOutput,
  Cluster,
  ClusterStats,
  MarkerClusterer,
  MarkerClustererEvents,
  MarkerUtils,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import { GoogleMapsService } from '../../../../services/google-maps.service';
import { TruncateDirective } from '../../../../shared/directives/truncate.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
declare const google: any;
type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

const markerIcons = {
  clusterIcon: 'assets/icons/map-cluster-marker.svg',
};

class MembershipAwareSuperClusterAlgorithm extends SuperClusterAlgorithm {
  private lastMembershipSignature: string | null = null;
  private markerIdentity = new WeakMap<object, number>();
  private markerIdentityCounter = 1;

  public override calculate(input: AlgorithmInput): AlgorithmOutput {
    const baseResult = super.calculate(input);
    const membershipSignature = this.createMembershipSignature(baseResult.clusters);
    const changed = membershipSignature !== this.lastMembershipSignature;
    this.lastMembershipSignature = membershipSignature;

    return {
      ...baseResult,
      changed,
    };
  }

  private createMembershipSignature(clusters: Cluster[]): string {
    const clusterKeys = clusters.map((cluster) => {
      const memberIds = cluster.markers
        .map((marker) => this.getMarkerIdentity(marker as unknown as object))
        .sort((a, b) => a - b);

      return memberIds.join(',');
    });

    clusterKeys.sort();
    return clusterKeys.join('|');
  }

  private getMarkerIdentity(marker: object): number {
    const existingIdentity = this.markerIdentity.get(marker);
    if (existingIdentity) {
      return existingIdentity;
    }

    const newIdentity = this.markerIdentityCounter++;
    this.markerIdentity.set(marker, newIdentity);
    return newIdentity;
  }
}

class MembershipAwareMarkerClusterer extends MarkerClusterer {
  private clusterMarkerIdentity = new WeakMap<object, number>();
  private clusterMarkerIdentityCounter = 1;

  public override render(): void {
    const map = this.getMap();
    if (!(map instanceof google.maps.Map)) {
      return;
    }
    const mapInstance = map as google.maps.Map;
    if (!mapInstance.getProjection()) {
      return;
    }

    google.maps.event.trigger(this, MarkerClustererEvents.CLUSTERING_BEGIN, this);

    const { clusters } = this.algorithm.calculate({
      markers: this.markers,
      map: mapInstance,
      mapCanvasProjection: this.getProjection(),
    });

    const previousClusters = this.clusters;
    const nextSingleMarkers = new Set(
      clusters.filter((cluster) => cluster.markers.length === 1).map((cluster) => cluster.markers[0]),
    );

    const reusableClusterMarkersBySignature = new Map<string, google.maps.Marker | AdvancedMarkerElement>();
    for (const cluster of previousClusters) {
      if (cluster.markers.length <= 1 || !cluster.marker) {
        continue;
      }
      reusableClusterMarkersBySignature.set(
        this.getClusterSignature(cluster),
        cluster.marker as google.maps.Marker | AdvancedMarkerElement,
      );
    }

    for (const cluster of clusters) {
      if (cluster.markers.length <= 1) {
        continue;
      }
      const signature = this.getClusterSignature(cluster);
      const reusableMarker = reusableClusterMarkersBySignature.get(signature);
      if (reusableMarker) {
        cluster.marker = reusableMarker;
        reusableClusterMarkersBySignature.delete(signature);
      }
    }

    for (const cluster of previousClusters) {
      if (!cluster.marker) {
        continue;
      }

      if (cluster.markers.length === 1) {
        if (!nextSingleMarkers.has(cluster.marker)) {
          MarkerUtils.setMap(cluster.marker, null);
        }
        continue;
      }

      if (reusableClusterMarkersBySignature.has(this.getClusterSignature(cluster))) {
        MarkerUtils.setMap(cluster.marker, null);
      }
    }

    this.clusters = clusters;
    this.renderClustersSelective();

    google.maps.event.trigger(this, MarkerClustererEvents.CLUSTERING_END, this);
  }

  private renderClustersSelective(): void {
    const stats = new ClusterStats(this.markers, this.clusters);
    const map = this.getMap();
    if (!(map instanceof google.maps.Map)) {
      return;
    }
    const mapInstance = map as google.maps.Map;

    this.clusters.forEach((cluster) => {
      if (cluster.markers.length === 1) {
        cluster.marker = cluster.markers[0];
      } else if (!cluster.marker) {
        cluster.marker = this.renderer.render(cluster, stats, mapInstance);
        cluster.markers.forEach((marker) => MarkerUtils.setMap(marker, null));

        if (this.onClusterClick) {
          const markerClickEventName = MarkerUtils.isAdvancedMarker(cluster.marker)
            ? MarkerClustererEvents.GMP_CLICK
            : MarkerClustererEvents.CLUSTER_CLICK;

          cluster.marker.addListener(markerClickEventName, (event: google.maps.MapMouseEvent) => {
            google.maps.event.trigger(this, MarkerClustererEvents.CLUSTER_CLICK, cluster);
            this.onClusterClick(event, cluster, mapInstance);
          });
        }
      } else {
        cluster.markers.forEach((marker) => MarkerUtils.setMap(marker, null));
      }

      MarkerUtils.setMap(cluster.marker, mapInstance);
    });
  }

  private getClusterSignature(cluster: Cluster): string {
    const memberIds = cluster.markers
      .map((marker) => this.getMarkerIdentity(marker as unknown as object))
      .sort((a, b) => a - b);

    return memberIds.join(',');
  }

  private getMarkerIdentity(marker: object): number {
    const existingIdentity = this.clusterMarkerIdentity.get(marker);
    if (existingIdentity) {
      return existingIdentity;
    }

    const newIdentity = this.clusterMarkerIdentityCounter++;
    this.clusterMarkerIdentity.set(marker, newIdentity);
    return newIdentity;
  }
}

@Component({
  selector: 'organization-custom-objects-map',
  templateUrl: './organization-custom-objects-map.component.html',
  styleUrl: './organization-custom-objects-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, GoogleMap, MapInfoWindow, TruncateDirective],
})
export class OrganizationCustomObjectsMapComponent implements OnInit {
  @ViewChild('googleMap', { static: true }) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  public selectIsCreating = this.store$.selectSignal(MapCustomObjectSelectors.selectIsCreating);
  public selectIsDeleting = this.store$.selectSignal(MapCustomObjectSelectors.selectIsDeleting);
  public selectCoords$ = this.store$.pipe(select(MapCustomObjectSelectors.selectCoords));

  public isInitialLoading = true;

  public selectObjects = this.store$.selectSignal(MapCustomObjectSelectors.selectAllMapCustomObjects);
  public mapsCoords = signal<{ lat: number; lng: number }>({ lat: 37.7749, lng: -122.4194 });
  private geocoder: google.maps.Geocoder;
  private markers: AdvancedMarkerElement[] = [];
  private deviceCluster;
  private draggedMarkerIds = new Set<string>();
  public markerName = signal(null);
  public isMapReadySig = signal(false);
  constructor(
    private store$: Store,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private googleMapsService: GoogleMapsService,
    private destroyRef: DestroyRef,
  ) {
    effect(() => {
      const objects = this.selectObjects();
      if (this.googleMap?.googleMap && objects) {
        this.renderMarkers(objects);
        if (objects.length > 0) {
          const id = this.route.snapshot.paramMap.get('id');
          let targetCoords = objects[0]?.mapConfig?.coords;

          if (id) {
            const obj = objects.find((o) => o._id === id);
            if (obj) {
              targetCoords = obj.mapConfig.coords;
            }
          }

          if (targetCoords && this.isInitialLoading) {
            this.mapsCoords.set(targetCoords);
            this.isInitialLoading = false;
          }
        }
        this.isMapReadySig.set(true);
      }
    });
  }

  ngOnInit() {
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.mapObjects }));
    this.store$.dispatch(MapCustomObjectsActions.getAllObjects());
    this.geocoder = new google.maps.Geocoder();

    this.selectCoords$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((coords) => {
      if (coords) {
        this.mapsCoords.set(coords);
      }
    });
  }

  public async openCreateObjectModal(
    object?: MapCustomObject,
  ): Promise<MatDialogRef<CustomObjectsModalComponent> | void> {
    let modalData: ModalData;
    if (object) {
      modalData = {
        isEdit: true,
        _id: object._id,
        name: object.name,
        mapConfig: object.mapConfig,
      };
    }

    const dialogRef = this.dialog.open(CustomObjectsModalComponent, {
      panelClass: 'modal-no-padding',
      width: '400px',
      data: modalData || { isEdit: false },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        if (object?._id) {
          const prevAddress = (object.mapConfig?.address ?? '').trim();
          const nextAddress = (res.address ?? '').trim();

          if (prevAddress === nextAddress) {
            this.draggedMarkerIds.delete(object._id);
          }
        }
        this.mapsCoords.set(res.coords);
      }
    });

    return dialogRef;
  }

  private renderMarkers(customObjects: MapCustomObjectsModels.MapCustomObject[]) {
    const geocoder = new google.maps.Geocoder();
    this.markers.forEach((marker) => (marker.map = null));
    this.markers = [];
    if (this.deviceCluster) {
      this.deviceCluster.clearMarkers();
    }
    const processedDraggedIds = new Set<string>();

    const { objects, objectIdOffset, objectIdRadius } = this.googleMapsService.addRadius(customObjects);
    const existingIds = new Set(objects.map((obj) => obj._id));
    this.draggedMarkerIds.forEach((id) => {
      if (!existingIds.has(id)) {
        this.draggedMarkerIds.delete(id);
      }
    });

    objects.forEach((obj) => {
      const markerContent = document.createElement('div');
      markerContent.className = 'custom-marker-wrapper';
      let dragStartPosition: google.maps.LatLngLiteral | null = null;

      let coords = { ...obj.mapConfig.coords };
      const isDraggedMarker = this.draggedMarkerIds.has(obj._id);
      const transformDeg = isDraggedMarker ? 0 : objectIdOffset[obj._id];
      const radius = isDraggedMarker ? 0 : objectIdRadius[obj._id];
      if (isDraggedMarker) {
        processedDraggedIds.add(obj._id);
      }

      markerContent.innerHTML = `
        <div class="custom-marker" style="background-color: ${obj.mapConfig.markerColor};">
          <span class="ui-icon-map-object"></span>
        </div>
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.googleMap.googleMap,
        position: calculateOffset(coords, radius, transformDeg),
        content: markerContent,
        gmpDraggable: true,
      });

      (marker as any).customData = obj;

      marker.addListener('click', () => {
        this.openCreateObjectModal(obj);
      });

      marker.content.addEventListener('mouseenter', () => {
        this.showObjectPopup(marker, obj);
      });

      marker.content.addEventListener('mouseleave', () => {
        this.onObjectMarkerHoverOut();
      });

      marker.addListener('dragstart', () => {
        dragStartPosition = this.toLatLngLiteral(marker.position);
      });

      marker.addListener('dragend', async () => {
        const newPosition = this.toLatLngLiteral(marker.position);
        if (!newPosition) {
          return;
        }

        if (!dragStartPosition || !this.hasMeaningfulCoordsDiff(dragStartPosition, newPosition)) {
          dragStartPosition = null;
          return;
        }

        const newCoords = {
          lat: newPosition.lat,
          lng: newPosition.lng,
        };

        const address = await geocoder.geocode({ location: newCoords });
        const formattedAddress = address.results?.[0]?.formatted_address || '';
        const updateObject: MapCustomObjectUpdateData = {
          _id: marker.customData._id,
          name: marker.customData.name,
          mapConfig: {
            ...marker.customData.mapConfig,
            address: formattedAddress,
            coords: newCoords,
            markerColor: marker.customData.mapConfig.markerColor,
          },
        };
        this.draggedMarkerIds.add(updateObject._id);
        this.mapsCoords.set(updateObject.mapConfig.coords);
        this.store$.dispatch(MapCustomObjectsActions.startUpdateObject({ object: updateObject }));
        dragStartPosition = null;
      });

      this.markers.push(marker);
    });

    processedDraggedIds.forEach((id) => this.draggedMarkerIds.delete(id));
    this.renderCluster();
  }

  public showObjectPopup(marker: AdvancedMarkerElement, obj: MapCustomObject): void {
    this.markerName.set(obj.name);
    this.infoWindow.infoWindow.open({
      anchor: marker,
    });
  }

  public onObjectMarkerHoverOut(): void {
    this.markerName.set('');
    this.infoWindow.close();
  }

  private renderCluster(): void {
    const mapInstance = this.googleMap.googleMap;
    if (!this.deviceCluster) {
      const customRenderer = {
        render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
          const markerContent = document.createElement('div');
          markerContent.innerHTML = `
            <div class="app-cluster-marker">
              <img class="app-cluster-icon" src="${markerIcons.clusterIcon}" alt="Cluster" />
            <span class="app-cluster-number">${count}</span>
          </div>`;
          const marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            map: mapInstance,
            content: markerContent,
          });

          return marker;
        },
      };
      this.deviceCluster = new MembershipAwareMarkerClusterer({
        markers: this.markers,
        map: mapInstance,
        renderer: customRenderer,
        algorithm: new MembershipAwareSuperClusterAlgorithm({
          radius: 80,
          maxZoom: 18,
        }),
      });
    } else {
      this.deviceCluster.clearMarkers();
      this.deviceCluster.addMarkers(this.markers);
    }
  }

  private toLatLngLiteral(position: google.maps.LatLng | google.maps.LatLngLiteral | undefined): google.maps.LatLngLiteral | null {
    if (!position) {
      return null;
    }
    if (typeof (position as google.maps.LatLng).lat === 'function') {
      const latLng = position as google.maps.LatLng;
      return {
        lat: latLng.lat(),
        lng: latLng.lng(),
      };
    }
    return position as google.maps.LatLngLiteral;
  }

  private hasMeaningfulCoordsDiff(
    prev: google.maps.LatLngLiteral,
    next: google.maps.LatLngLiteral,
    epsilon = 0.0000005,
  ): boolean {
    return Math.abs(prev.lat - next.lat) > epsilon || Math.abs(prev.lng - next.lng) > epsilon;
  }
}

const calculateOffset = (
  center: google.maps.LatLngLiteral,
  radius: number,
  angle: number,
): google.maps.LatLngLiteral => {
  const angleInRadians = (angle * Math.PI) / 180;

  const latOffset = radius * Math.cos(angleInRadians);
  const lngOffset = radius * Math.sin(angleInRadians);

  return {
    lat: center.lat + latOffset,
    lng: center.lng + lngOffset,
  };
};
